import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { COLORS, ENTITY_COLORS, RAMP_COLORS } from '../theme';

const fetchPortfolio = async (sheet) => {
  const response = await fetch(`/api/getSheetData?sheet=${encodeURIComponent(sheet)}`);
  const data = await response.json();
  return data;
};

const parseHoldings = (rows) => {
  if (!rows || !Array.isArray(rows)) return [];
  return rows.slice(1).filter(row => row[2]).map(row => ({
    ticker: row[2],
    name: row[3],
    yield: row[4],
    shares: row[5],
    avgCost: row[6],
    marketValue: row[7],
  }));
};

const formatCurrency = (val) => {
  if (typeof val === 'number') return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return val || '--';
};

const formatPercent = (val) => {
  if (typeof val === 'number') return `${(val * 100).toFixed(2)}%`;
  if (typeof val === 'string') return val;
  return '--';
};

// Groups the smallest positions into a single "Other" slice so the chart stays legible.
const buildAllocation = (fjcHoldings, fjhHoldings) => {
  const all = [
    ...fjcHoldings.map(h => ({ ...h, entity: 'FJC' })),
    ...fjhHoldings.map(h => ({ ...h, entity: 'FJH' })),
  ].filter(h => typeof h.marketValue === 'number' && h.marketValue > 0)
    .sort((a, b) => b.marketValue - a.marketValue);

  const top = all.slice(0, 7);
  const rest = all.slice(7);
  const restTotal = rest.reduce((sum, h) => sum + h.marketValue, 0);

  const slices = top.map((h, i) => ({ name: h.ticker, value: h.marketValue, entity: h.entity, color: RAMP_COLORS[i % RAMP_COLORS.length] }));
  if (restTotal > 0) {
    slices.push({ name: 'Other', value: restTotal, entity: null, color: '#c9cfd6' });
  }
  return slices;
};

const EntityBadge = ({ entity }) => (
  <span style={{ ...styles.entityBadge, backgroundColor: ENTITY_COLORS[entity] }}>{entity}</span>
);

const HoldingsTable = ({ holdings, totalMarketValue }) => {
  const portfolioPercent = (marketValue) => {
    if (typeof marketValue !== 'number' || totalMarketValue === 0) return '--';
    return `${((marketValue / totalMarketValue) * 100).toFixed(1)}%`;
  };

  const sectionTotal = holdings.reduce((sum, h) => sum + (typeof h.marketValue === 'number' ? h.marketValue : 0), 0);

  if (holdings.length === 0) {
    return <div style={styles.empty}>No holdings data available.</div>;
  }

  return (
    <div style={styles.tableWrapper} className="fj-card">
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Ticker</th>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Distribution Yield (TTM)</th>
            <th style={styles.th}>Shares</th>
            <th style={styles.th}>Avg. Cost</th>
            <th style={styles.th}>Market Value</th>
            <th style={styles.th}>% of Portfolio</th>
          </tr>
        </thead>
        <tbody>
          {holdings.map((holding, index) => (
            <tr key={holding.ticker} style={index % 2 === 0 ? styles.rowEven : styles.rowOdd} className="fj-row">
              <td style={styles.tdTicker}>{holding.ticker}</td>
              <td style={styles.td}>{holding.name}</td>
              <td style={styles.tdCenter}>{formatPercent(holding.yield)}</td>
              <td style={styles.tdCenter}>{typeof holding.shares === 'number' ? holding.shares.toLocaleString() : holding.shares}</td>
              <td style={styles.tdCenter}>{formatCurrency(holding.avgCost)}</td>
              <td style={styles.tdCenter}>{formatCurrency(holding.marketValue)}</td>
              <td style={styles.tdCenter}>{portfolioPercent(holding.marketValue)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={styles.sectionTotalRow}>
            <td style={styles.tdTotal} colSpan={5}>Subtotal</td>
            <td style={styles.tdTotalValue}>{formatCurrency(sectionTotal)}</td>
            <td style={styles.tdTotalValue}>{portfolioPercent(sectionTotal)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const Portfolio = () => {
  const [fjcHoldings, setFjcHoldings] = useState([]);
  const [fjhHoldings, setFjhHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [fjcRows, fjhRows] = await Promise.all([
          fetchPortfolio('FJC Portfolio'),
          fetchPortfolio('FJH Portfolio'),
        ]);
        setFjcHoldings(parseHoldings(fjcRows));
        setFjhHoldings(parseHoldings(fjhRows));
      } catch (e) {
        setError('Failed to load portfolio data.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const allHoldings = [...fjcHoldings, ...fjhHoldings];
  const totalMarketValue = allHoldings.reduce((sum, h) => sum + (typeof h.marketValue === 'number' ? h.marketValue : 0), 0);
  const allocation = buildAllocation(fjcHoldings, fjhHoldings);

  if (loading) return <div style={styles.loading}>Loading portfolio data...</div>;
  if (error) return <div style={styles.loading}>{error}</div>;

  return (
    <div style={styles.container}>
      <style>{PORTFOLIO_STYLES}</style>

      <div style={styles.header}>
        <h1 style={styles.title}>Portfolio</h1>
        <div style={styles.divider} />
        <p style={styles.subtitle}>
          Our current holdings reflect our commitment to yield-producing,
          income-generating assets with disciplined risk management.
        </p>
      </div>

      <div style={styles.content}>

        {/* Composition */}
        {allocation.length > 0 && (
          <div style={styles.compositionCard} className="fj-card">
            <h2 style={styles.sectionTitle}>Portfolio Composition</h2>
            <div style={styles.compositionBody}>
              <div style={styles.compositionChart}>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={allocation} dataKey="value" nameKey="name" innerRadius={68} outerRadius={108} paddingAngle={2} stroke="none">
                      {allocation.map(entry => <Cell key={entry.name} fill={entry.color} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [formatCurrency(v), n]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={styles.legendList}>
                {allocation.map(entry => (
                  <div key={entry.name} style={styles.legendRow}>
                    <span style={{ ...styles.legendDot, backgroundColor: entry.color }} />
                    <span style={styles.legendName}>{entry.name}</span>
                    {entry.entity && <EntityBadge entity={entry.entity} />}
                    <span style={styles.legendPercent}>
                      {totalMarketValue > 0 ? `${((entry.value / totalMarketValue) * 100).toFixed(1)}%` : '--'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* FJC Section */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <EntityBadge entity="FJC" /> Flying Jib Capital — Equity Holdings
          </h2>
          <HoldingsTable holdings={fjcHoldings} totalMarketValue={totalMarketValue} />
        </div>

        {/* FJH Section */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <EntityBadge entity="FJH" /> Flying Jib Holdings — Equity Holdings
          </h2>
          <HoldingsTable holdings={fjhHoldings} totalMarketValue={totalMarketValue} />
        </div>

        {/* Consolidated Total */}
        <div style={styles.totalBar}>
          <span style={styles.totalLabel}>Total Portfolio Value — All Entities</span>
          <span style={styles.totalValue}>{formatCurrency(totalMarketValue)}</span>
        </div>

        <div style={styles.disclaimer}>
          <p style={styles.disclaimerText}>
            Holdings and position data are updated following trade execution and reflect the most recent available pricing via real-time market data feeds. Market values are subject to continuous fluctuation and may not reflect intraday price movements at the time of viewing. This information is provided for transparency purposes only and does not constitute investment advice or a solicitation to buy or sell any security. Flying Jib Capital Inc. is not a registered investment adviser.
          </p>
        </div>
      </div>
    </div>
  );
};

const PORTFOLIO_STYLES = `
  .fj-card {
    transition: box-shadow 0.3s ease;
  }
  .fj-card:hover {
    box-shadow: 0 12px 28px rgba(10, 22, 40, 0.10);
  }
  .fj-row {
    transition: background-color 0.15s ease;
  }
  .fj-row:hover {
    background-color: #eef2f7 !important;
  }
`;

const styles = {
  container: { minHeight: '90vh', backgroundColor: COLORS.bg, padding: '60px 40px' },
  loading: { minHeight: '90vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Georgia, serif', fontSize: '18px', color: COLORS.navy, backgroundColor: COLORS.bg },
  header: { maxWidth: '1000px', margin: '0 auto 48px auto', textAlign: 'center' },
  title: { fontSize: '42px', fontWeight: '700', color: COLORS.navy, fontFamily: 'Georgia, serif', letterSpacing: '1px', marginBottom: '16px' },
  divider: { width: '60px', height: '3px', backgroundColor: COLORS.navy, margin: '0 auto 24px auto' },
  subtitle: { fontSize: '16px', color: COLORS.slate, fontFamily: 'Georgia, serif', lineHeight: '1.8', maxWidth: '600px', margin: '0 auto' },
  content: { maxWidth: '1000px', margin: '0 auto' },
  compositionCard: { backgroundColor: '#ffffff', padding: '32px', marginBottom: '40px', boxShadow: '0 1px 3px rgba(10, 22, 40, 0.06)' },
  compositionBody: { display: 'flex', gap: '32px', alignItems: 'center', flexWrap: 'wrap' },
  compositionChart: { flex: '1 1 280px', minWidth: '240px' },
  legendList: { flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: '10px' },
  legendRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  legendDot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0 },
  legendName: { fontSize: '14px', fontWeight: '700', color: COLORS.navy, fontFamily: 'Georgia, serif', minWidth: '48px' },
  legendPercent: { fontSize: '13px', color: COLORS.slate, fontFamily: 'Georgia, serif', marginLeft: 'auto' },
  entityBadge: { display: 'inline-block', fontSize: '10px', fontWeight: '700', color: '#ffffff', fontFamily: 'Georgia, serif', letterSpacing: '0.5px', padding: '2px 7px', borderRadius: '3px', verticalAlign: 'middle', marginRight: '4px' },
  section: { marginBottom: '40px' },
  sectionTitle: { fontSize: '18px', fontWeight: '700', color: COLORS.navy, fontFamily: 'Georgia, serif', letterSpacing: '0.5px', marginBottom: '16px', paddingBottom: '12px', borderBottom: `2px solid ${COLORS.navy}`, display: 'flex', alignItems: 'center', gap: '4px' },
  empty: { backgroundColor: '#ffffff', padding: '32px', textAlign: 'center', fontFamily: 'Georgia, serif', color: COLORS.slate, fontSize: '14px', borderLeft: `3px solid ${COLORS.navy}` },
  tableWrapper: { overflowX: 'auto', boxShadow: '0 1px 3px rgba(10, 22, 40, 0.06)' },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: '#ffffff', fontFamily: 'Georgia, serif' },
  th: { backgroundColor: COLORS.navy, color: '#ffffff', padding: '14px 16px', fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase', textAlign: 'left', fontFamily: 'Georgia, serif', fontWeight: '400', whiteSpace: 'nowrap' },
  td: { padding: '14px 16px', fontSize: '14px', color: COLORS.navy, fontFamily: 'Georgia, serif', borderBottom: '1px solid #e8eef4' },
  tdTicker: { padding: '14px 16px', fontSize: '15px', fontWeight: '700', color: COLORS.navy, fontFamily: 'Georgia, serif', borderBottom: '1px solid #e8eef4', whiteSpace: 'nowrap' },
  tdCenter: { padding: '14px 16px', fontSize: '14px', color: COLORS.navy, fontFamily: 'Georgia, serif', borderBottom: '1px solid #e8eef4', textAlign: 'right', whiteSpace: 'nowrap' },
  rowEven: { backgroundColor: '#ffffff' },
  rowOdd: { backgroundColor: '#f8fafc' },
  sectionTotalRow: { backgroundColor: '#f0f4f8', borderTop: `1px solid ${COLORS.navy}` },
  tdTotal: { padding: '12px 16px', fontSize: '12px', fontWeight: '700', color: COLORS.navy, fontFamily: 'Georgia, serif', letterSpacing: '0.5px', textTransform: 'uppercase' },
  tdTotalValue: { padding: '12px 16px', fontSize: '14px', fontWeight: '700', color: COLORS.navy, fontFamily: 'Georgia, serif', textAlign: 'right' },
  totalBar: { backgroundColor: COLORS.navy, padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' },
  totalLabel: { fontSize: '13px', color: '#ffffff', fontFamily: 'Georgia, serif', letterSpacing: '1px', textTransform: 'uppercase' },
  totalValue: { fontSize: '22px', fontWeight: '700', color: '#ffffff', fontFamily: 'Georgia, serif' },
  disclaimer: { borderTop: '1px solid #d0d8e4', paddingTop: '24px' },
  disclaimerText: { fontSize: '12px', color: COLORS.slate, fontFamily: 'Georgia, serif', lineHeight: '1.8', textAlign: 'center', margin: 0 },
};

export default Portfolio;
